import {
  OrganizationType,
  rdbV1PersonSportRankingsList,
  rdbV1RecruitmentsLatestRpmPicksList,
  rdbV1TeamRankingConsensusTeamRankingsDetail,
  type RdbV1TeamRankingConsensusTeamRankingsDetailParams,
  rdbV2LatestList,
  SiteType,
  v1CategoriesFeaturedHeadlinesDetail,
  v1CategoriesHeadlinesDetail2,
  v1OrganizationCompositeRankingsList,
  v1SitesList,
} from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import { withAuthentication } from '@on3/ui-lib/src/hocs/withAuthentication';
import { withValidUrl } from '@on3/ui-lib/src/hocs/withValidUrl';
import {
  getFulfilledValue,
  proxyApi,
  proxyApiWithVary,
} from '@on3/ui-lib/utils/api';
import {
  type IRivalsPageProps,
  RivalsHome,
} from '@public/components/Home/Rivals';
import { PageWrapper } from '@public/components/Layouts/PageWrapper';
import { GraphSchema } from '@public/store/schemas/Graph';
import { OrganizationSchema } from '@public/store/schemas/Organization';
import type { AxiosError } from 'axios';
import type { GetServerSideProps } from 'next';

const title = 'The Best of Recruiting and High School Sports';
const description = `Rivals is the best resource for recruiting and high school sports. Find all of your favorite team's information or browse our advanced player database.`;

export interface IRivalsHomeProps extends IRivalsPageProps, IGenericPageProps {}

const NetworkHomePage = (props: IRivalsHomeProps) => {
  const canonical = 'https://www.on3.com/rivals/';

  const schema = new GraphSchema({
    items: [new OrganizationSchema().toJSON()],
  });

  return (
    <PageWrapper
      {...props}
      canonical={canonical}
      description={description}
      schema={schema}
      title={title}
    >
      <RivalsHome {...props} />
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx, 411);
  const userData = await withAuthentication.isAuth(ctx);
  const { requestWithVaryParams } = userData || {};

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  try {
    const year =
      siteData.defaultYears.highSchoolYear || new Date().getFullYear();
    const recruitingYear =
      siteData.defaultYears.footballRecruitingYear || new Date().getFullYear();

    const timeOut = {
      timeout: 2000,
    };

    const rivalsCategoryKey = siteData.currentSite.categoryKey ?? 571985;
    const footballRecruitingKey = 94;
    const basketballRecruitingKey = 96;
    const teamRecruitingParams = {
      sport: 'football',
      sportSlug: 'football',
      year: recruitingYear,
      limit: 10,
    } as RdbV1TeamRankingConsensusTeamRankingsDetailParams;
    const [
      featuredArticles,
      footballRecruitingArticles,
      basketballRecruitingArticles,
      highSchoolArticles,
      latestCommits,
      highSchoolRankings,
      fanSites,
      playerRankings,
      latestPredictions,
      teamRecruitingRankings,
    ] = await Promise.allSettled([
      v1CategoriesFeaturedHeadlinesDetail(proxyApi, rivalsCategoryKey, timeOut),
      v1CategoriesHeadlinesDetail2(proxyApi, {
        category: footballRecruitingKey,
        limit: 13,
      }),
      v1CategoriesHeadlinesDetail2(proxyApi, {
        category: basketballRecruitingKey,
        limit: 13,
      }),
      v1CategoriesHeadlinesDetail2(proxyApi, {
        category: 556984,
        limit: 8,
      }),

      rdbV2LatestList(proxyApi, {
        limit: 5,
      }),
      v1OrganizationCompositeRankingsList(proxyApi, {
        orgType: OrganizationType.HighSchool,
        items: 10,
        sportKey: 1,
        year,
      }),
      v1SitesList(proxyApi, {
        type: SiteType.Team,
      }),
      rdbV1PersonSportRankingsList(proxyApi, {
        sportKey: 1,
        year: recruitingYear,
        limit: 5,
      }),
      rdbV1RecruitmentsLatestRpmPicksList(
        proxyApiWithVary,
        {
          limit: 5,
          sportKey: 1,
        },
        requestWithVaryParams,
      ),
      rdbV1TeamRankingConsensusTeamRankingsDetail(
        proxyApi,
        teamRecruitingParams,
      ),
    ]);

    return {
      props: {
        pageType: 'home',
        siteData: siteData || [],
        userData: userData || {},
        featuredArticles: getFulfilledValue(featuredArticles),
        footballRecruitingArticles: getFulfilledValue(
          footballRecruitingArticles,
        ),
        basketballRecruitingArticles: getFulfilledValue(
          basketballRecruitingArticles,
        ),
        highSchoolArticles: getFulfilledValue(highSchoolArticles),
        latestCommits: getFulfilledValue(latestCommits),
        highSchoolRankings: getFulfilledValue(highSchoolRankings),
        fanSites: getFulfilledValue(fanSites),
        playerRankings: getFulfilledValue(playerRankings),
        latestPredictions: getFulfilledValue(latestPredictions),
        teamRecruitingRankings: getFulfilledValue(teamRecruitingRankings),
      },
    };
  } catch (error) {
    const err: AxiosError<unknown> = error as AxiosError<unknown>;

    console.log(err.message);

    return { props: {} };
  }
};

export default withValidUrl(NetworkHomePage);
